import { get } from '@ember/helper';
import { action } from '@ember/object';
import { trackedArray } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import { gt } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import SettingsBudgetForecastSegmentHierarchyColumn from 'lumen/components/settings/budget-forecast/segment-hierarchy/column';
import type { BudgetForecastSegment } from 'lumen/models';

const NUM_LEVELS = 3;

function propagateCheckedStateUpward(segment: BudgetForecastSegment): void {
  if (segment.parent) {
    segment.parent.visible = true;
    propagateCheckedStateUpward(segment.parent);
  }
}

function propagateUncheckedStateDownward(segment: BudgetForecastSegment): void {
  segment.children.forEach((child) => {
    child.visible = false;
    propagateUncheckedStateDownward(child);
  });
}

interface SettingsBudgetForecastSegmentHierarchySignature {
  Args: {
    segments: BudgetForecastSegment[];
  };
}

function getFirstItemAtLevel(
  segments: BudgetForecastSegment[],
  depth: number,
): BudgetForecastSegment | undefined {
  if (depth === 0) {
    return segments.find((segment) => segment.depth === depth);
  }

  const parent = getFirstItemAtLevel(segments, depth - 1);
  return parent?.children[0];
}

export default class SettingsBudgetForecastSegmentHierarchy extends Component<SettingsBudgetForecastSegmentHierarchySignature> {
  /**
   * The segments that are expanded in the hierarchy.
   * Initially, the first segment at each level is expanded.
   */
  expandedSegments = trackedArray<BudgetForecastSegment | null>(
    Array.from(
      { length: NUM_LEVELS - 1 },
      (_, index) => getFirstItemAtLevel(this.args.segments, index) ?? null,
    ),
  );

  get numberOfLevelsWithData(): number {
    return Array.from({ length: NUM_LEVELS }, (_, index) => index).filter(
      (depth) => this.args.segments.find((segment) => segment.depth === depth),
    ).length;
  }

  @action
  getSegmentItemsForDepth(depth: number): BudgetForecastSegment[] {
    if (depth === 0) {
      return this.args.segments.filter((segment) => segment.depth === depth);
    }

    const expandedParentSegment = this.expandedSegments[depth - 1];
    if (expandedParentSegment) {
      return expandedParentSegment.children;
    }

    return [];
  }

  @action
  onExpandSegment(segment: BudgetForecastSegment): void {
    const expandedSegmentAtDepth = this.expandedSegments[segment.depth];

    const isSegmentExpanded =
      expandedSegmentAtDepth && expandedSegmentAtDepth.id === segment.id;
    this.expandedSegments[segment.depth] = isSegmentExpanded ? null : segment;

    // Collapse all segments at a deeper level
    for (let i = segment.depth + 1; i < this.expandedSegments.length; i++) {
      this.expandedSegments[i] = null;
    }
  }

  @action
  toggleSegmentCheckbox(segment: BudgetForecastSegment): void {
    segment.visible = !segment.visible;

    if (segment.visible) {
      propagateCheckedStateUpward(segment);
    } else {
      propagateUncheckedStateDownward(segment);
    }
  }

  <template>
    <div
      class="rounded-200 border-neutral-default grid auto-cols-fr grid-flow-col border border-solid"
      data-test-id="segment-hierarchy"
    >
      <IncrementFor @times={{this.numberOfLevelsWithData}} as |depth|>
        <div
          class={{if
            (gt depth 0)
            "border-neutral-default border-l border-solid"
          }}
        >
          <SettingsBudgetForecastSegmentHierarchyColumn
            @depth={{depth}}
            @expandedSegment={{get this.expandedSegments depth}}
            @onExpandSegment={{this.onExpandSegment}}
            @onToggleSegmentCheckbox={{this.toggleSegmentCheckbox}}
            @segments={{this.getSegmentItemsForDepth depth}}
          />
        </div>
      </IncrementFor>
    </div>
  </template>
}
